import { feedAge } from "../lib/feedFreshness";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FallbackTier, NormalizedFeed } from "@chonburi/shared";
import { clampPollMs, startGuardedPoll } from "../lib/pollGuard";

interface FeedState<T> {
  data: T[];
  ageMinutes: number;
  fallbackTier: FallbackTier | "loading";
  loadedAt: string | null;
  error: string | null;
  /** Upstream `meta.note` — explains *why* a feed is degraded (e.g. "Missing
   *  AISSTREAM_TOKEN"). Surfaced in the UI so a dead toggle is never silent. */
  note?: string;
  /** Upstream `meta.source` — the feed's own id, so a panel can name the
   *  authority that answered instead of hard-coding one. */
  source?: string;
}

const STORAGE_PREFIX = "chonburi:feed:";
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;
const UNAVAILABLE_AFTER_FAILS = 3;
const REQUEST_TIMEOUT_MS = 10_000;

function storageKey(path: string): string {
  return `${STORAGE_PREFIX}${path}`;
}

function readLocal<T>(path: string): FeedState<T> | null {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(storageKey(path)) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state: FeedState<T>; savedAt: number };
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > MAX_CACHE_AGE_MS) return null;
    return { ...parsed.state, ageMinutes: feedAge(parsed.state.loadedAt, parsed.state.ageMinutes), fallbackTier: "cache" };
  } catch {
    return null;
  }
}

function writeLocal<T>(path: string, state: FeedState<T>): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey(path), JSON.stringify({ state, savedAt: Date.now() }));
  } catch {}
}

const emptyInitial = <T,>(): FeedState<T> => ({
  data: [],
  ageMinutes: 0,
  fallbackTier: "loading",
  loadedAt: null,
  error: null,
});

async function fetchOnce(url: string, signal: AbortSignal): Promise<Response> {
  const ctrl = new AbortController();
  let timedOut = false;
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, REQUEST_TIMEOUT_MS);
  const abort = () => ctrl.abort();
  signal.addEventListener("abort", abort, { once: true });
  try {
    return await fetch(url, { signal: ctrl.signal, cache: "no-store" });
  } catch (err) {
    if (timedOut) throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s`);
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
    signal.removeEventListener("abort", abort);
  }
}

function isAbort(err: unknown): boolean {
  return (err instanceof DOMException && err.name === "AbortError")
    || (err instanceof Error && err.name === "AbortError");
}

export function useFeed<T>(path: string, pollMs: number): FeedState<T> & { refetch: () => void } {
  const [state, setState] = useState<FeedState<T>>(() => readLocal<T>(path) ?? emptyInitial<T>());
  const inflight = useRef<AbortController | null>(null);
  const kickRef = useRef<() => void>(() => {});
  const failCount = useRef(0);

  useEffect(() => {
    let cancelled = false;
    failCount.current = 0;
    const intervalMs = clampPollMs(pollMs);

    const poll = startGuardedPoll(async () => {
      if (cancelled) return "skip";
      inflight.current?.abort();
      const ctrl = new AbortController();
      inflight.current = ctrl;
      try {
        const sep = path.includes("?") ? "&" : "?";
        const url = `${path}${sep}_=${Date.now()}`;
        const res = await fetchOnce(url, ctrl.signal);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = (await res.json()) as NormalizedFeed<T>;
        if (cancelled) return "skip";
        failCount.current = 0;
        setState((prev) => {
          // Short-circuit when upstream hasn't moved — same fetchedAt means same payload.
          // Avoids cascading re-renders of dashboard chips and tickers on every poll.
          if (
            prev.loadedAt === json.meta.fetchedAt &&
            prev.fallbackTier === json.meta.fallbackTier &&
            prev.ageMinutes === feedAge(json.meta.fetchedAt, json.meta.ageMinutes) &&
            prev.note === json.meta.note &&
            prev.source === json.meta.source &&
            prev.error === null
          ) {
            return prev;
          }
          const next: FeedState<T> = {
            data: json.features,
            ageMinutes: feedAge(json.meta.fetchedAt, json.meta.ageMinutes),
            fallbackTier: json.meta.fallbackTier,
            loadedAt: json.meta.fetchedAt,
            error: null,
            note: json.meta.note,
            source: json.meta.source,
          };
          writeLocal(path, next);
          return next;
        });
        return "ok";
      } catch (err) {
        if (cancelled || isAbort(err)) return "skip";
        failCount.current += 1;
        const errMsg = err instanceof Error ? err.message : String(err);
        const fails = failCount.current;
        setState((prev) => {
          const tier: FallbackTier | "loading" =
            fails >= UNAVAILABLE_AFTER_FAILS
              ? "unavailable"
              : prev.loadedAt
                ? "cache"
                : "loading";
          const ageMinutes = feedAge(prev.loadedAt, prev.ageMinutes);
          if (prev.error === errMsg && prev.fallbackTier === tier && prev.ageMinutes === ageMinutes) return prev;
          return { ...prev, ageMinutes, error: errMsg, fallbackTier: tier };
        });
        return "fail";
      }
    }, intervalMs);

    kickRef.current = () => {
      inflight.current?.abort();
      poll.kick();
    };

    const onVis = () => {
      if (typeof document !== "undefined" && document.hidden) inflight.current?.abort();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      poll.stop();
      document.removeEventListener("visibilitychange", onVis);
      inflight.current?.abort();
    };
  }, [path, pollMs]);

  const refetch = useCallback(() => kickRef.current(), []);
  return { ...state, refetch };
}
